import { ApolloError, useApolloClient } from '@apollo/client'
import { toast } from 'sonner'
import { useConfirmation } from '@/context/ConfirmationContext'
import {
  DELETE_ADDITIONAL_COMPONENT_GROUP,
  GET_ADDITIONAL_COMPONENT_GROUP_USAGE,
} from './additionalComponentGroupQueries'
import {
  DELETE_ADDITIONAL_COMPONENT,
  GET_ADDITIONAL_COMPONENT_USAGE,
} from './additionalComponentQueries'

/*
 * Delete flow for component groups and components, shared by the group list,
 * the group form, the group details table and the component edit form.
 *
 * Groups (and components) are shared across products, so before confirming we
 * fetch cheap usage counts from the backend and put them in the dialog. The
 * backend refuses the delete when an order/offer line references a component;
 * we warn up front and surface the backend's own message if it refuses.
 */

type Kind = 'group' | 'component'

interface Usage {
  id: number
  code?: string | null
  productCount: number
  componentCount: number
  orderLineCount: number
  offerLineCount: number
  cartLineCount: number
  blocked: boolean
}

export function extractErrorMessage(error: unknown): string {
  if (error instanceof ApolloError) {
    return (
      error.graphQLErrors[0]?.message ||
      error.networkError?.message ||
      error.message
    )
  }
  if (error instanceof Error) return error.message
  return 'The request failed for an unknown reason.'
}

const CONFIG = {
  group: {
    usageQuery: GET_ADDITIONAL_COMPONENT_GROUP_USAGE,
    usageField: 'additionalComponentGroupUsage',
    mutation: DELETE_ADDITIONAL_COMPONENT_GROUP,
    noun: 'component group',
    refetchQueries: [
      'GET_ADDITIONAL_COMPONENT_GROUPS',
      'GET_ADDITIONAL_COMPONENT_GROUP',
    ],
  },
  component: {
    usageQuery: GET_ADDITIONAL_COMPONENT_USAGE,
    usageField: 'additionalComponentUsage',
    mutation: DELETE_ADDITIONAL_COMPONENT,
    noun: 'component',
    refetchQueries: [
      'GET_GROUP_COMPONENTS',
      'GET_GROUP_COMPONENTS_COUNT',
      'GET_ADDITIONAL_COMPONENT_GROUPS',
    ],
  },
} as const

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

function describe(kind: Kind, usages: Usage[], requested: number): string {
  const noun = CONFIG[kind].noun
  const deletable = usages.filter(u => !u.blocked)
  const blocked = usages.filter(u => u.blocked)
  const sum = (key: keyof Usage) =>
    deletable.reduce((acc, u) => acc + (Number(u[key]) || 0), 0)

  const parts: string[] = []
  if (requested === 1) {
    const name = usages[0]?.code ? ` "${usages[0].code}"` : ''
    parts.push(`Delete ${noun}${name}?`)
  } else {
    parts.push(`Delete ${plural(requested, noun)}?`)
  }

  if (deletable.length > 0) {
    const products = sum('productCount')
    parts.push(
      products > 0
        ? `It is used by ${plural(products, 'product')} and will be removed from all of them, including its prices.`
        : 'It is not used by any product.'
    )
    if (kind === 'group') {
      const components = sum('componentCount')
      if (components > 0) {
        parts.push(`${plural(components, 'component')} in the group will be deleted too.`)
      }
    }
    const carts = sum('cartLineCount')
    if (carts > 0) {
      parts.push(`${plural(carts, 'open cart/wishlist line')} will lose the component.`)
    }
  }

  if (blocked.length > 0) {
    const names = blocked.map(u => u.code || `#${u.id}`).join(', ')
    parts.push(
      `${blocked.length === requested && requested === 1 ? 'It' : names} cannot be deleted: referenced by existing orders/offers, which must keep their components. ` +
        (deletable.length > 0 ? 'Those will be skipped.' : '')
    )
  } else if (usages.length < requested) {
    parts.push('Usage could not be checked for every item.')
  }

  parts.push('This action cannot be undone.')
  return parts.join(' ')
}

export function useAdditionalComponentDelete(kind: Kind) {
  const client = useApolloClient()
  const { showConfirmation } = useConfirmation()
  const cfg = CONFIG[kind]

  const fetchUsage = async (id: number): Promise<Usage | null> => {
    try {
      const res = await client.query({
        query: cfg.usageQuery,
        variables: { id },
        fetchPolicy: 'network-only',
      })
      return (res.data as Record<string, Usage | undefined>)?.[cfg.usageField] ?? null
    } catch {
      return null
    }
  }

  /**
   * Ask for confirmation (with usage counts) and delete the given ids one by
   * one. Resolves after the dialog is shown; `onDeleted` runs once when at
   * least one id was deleted.
   */
  const confirmDelete = async (
    ids: number[],
    onDeleted?: (deletedIds: number[]) => void
  ) => {
    if (ids.length === 0) return
    const usages = (await Promise.all(ids.map(fetchUsage))).filter(
      (u): u is Usage => u !== null
    )

    // Every requested item is known to be blocked: explain instead of offering
    // a delete that the backend would refuse anyway.
    if (usages.length === ids.length && usages.every(u => u.blocked)) {
      const u = usages[0]
      toast.error(
        ids.length === 1
          ? `Cannot delete ${cfg.noun} "${u.code || `#${u.id}`}": it is referenced by ${plural(u.orderLineCount, 'order line')} and ${plural(u.offerLineCount, 'offer line')}. Disable it on the products instead.`
          : `None of the selected ${cfg.noun}s can be deleted: they are referenced by existing orders/offers. Disable them on the products instead.`,
        { duration: 10000 }
      )
      return
    }

    showConfirmation({
      title: `Delete ${cfg.noun}${ids.length === 1 ? '' : 's'}`,
      description: describe(kind, usages, ids.length),
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        const deleted: number[] = []
        const failures: string[] = []
        for (const id of ids) {
          try {
            await client.mutate({ mutation: cfg.mutation, variables: { id } })
            deleted.push(id)
          } catch (error) {
            failures.push(extractErrorMessage(error))
          }
        }
        if (deleted.length > 0) {
          await client
            .refetchQueries({ include: [...cfg.refetchQueries] })
            .catch(() => undefined)
          toast.success(
            deleted.length === 1
              ? `${cfg.noun[0].toUpperCase()}${cfg.noun.slice(1)} deleted`
              : `${plural(deleted.length, cfg.noun)} deleted`
          )
          onDeleted?.(deleted)
        }
        failures.forEach(message => toast.error(message, { duration: 10000 }))
      },
    })
  }

  return { confirmDelete }
}
